'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import WizardProgressHeader from './WizardProgressHeader';
import BasicInfoStep from './BasicInfoStep';
import BPMNModelingStep from './BPMNModelingStep';
import FormAssociationStep from './FormAssociationStep';
import ReviewStep from './ReviewStep';

interface WizardStep {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface BasicInfoData {
  processName: string;
  processDescription: string;
  category: string;
  department: string;
  priority: string;
  estimatedDuration: string;
  tags: string[];
}

interface BPMNElement {
  id: string;
  type: 'start' | 'task' | 'gateway' | 'end';
  label: string;
  x: number;
  y: number;
}

interface FieldMapping {
  formFieldId: string;
  bpmnElementId: string;
}

const WizardInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isStepValid, setIsStepValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string>('');

  const [basicInfo, setBasicInfo] = useState<BasicInfoData>({
    processName: '',
    processDescription: '',
    category: '',
    department: '',
    priority: '',
    estimatedDuration: '',
    tags: [],
  });

  const [bpmnElements, setBpmnElements] = useState<BPMNElement[]>([]);
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const autoSaveInterval = setInterval(() => {
      handleAutoSave();
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [isHydrated, basicInfo, bpmnElements, selectedForms, fieldMappings]);

  const steps: WizardStep[] = [
    {
      id: 1,
      title: 'Informações Básicas',
      description: 'Defina nome, categoria e detalhes do processo',
      icon: 'InformationCircleIcon',
    },
    {
      id: 2,
      title: 'Modelagem BPMN',
      description: 'Crie o diagrama do fluxo do processo',
      icon: 'CubeIcon',
    },
    {
      id: 3,
      title: 'Associação de Formulários',
      description: 'Vincule formulários e mapeie campos',
      icon: 'DocumentTextIcon',
    },
    {
      id: 4,
      title: 'Revisão',
      description: 'Revise e finalize o processo',
      icon: 'CheckCircleIcon',
    },
  ];

  const handleAutoSave = () => {
    if (!isHydrated) return;

    console.log('Auto-saving process data...');
    const now = new Date();
    const timeString = now.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
    setLastSaved(timeString);
  };

  const handleNext = () => {
    if (!isHydrated || !isStepValid) return;

    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setIsStepValid(currentStep === 3);
    }
  };

  const handlePrevious = () => {
    if (!isHydrated) return;

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setIsStepValid(true);
    }
  };

  const handleStepClick = (stepId: number) => {
    if (!isHydrated) return;
    setCurrentStep(stepId);
  };

  const handleFinish = async () => {
    if (!isHydrated) return;

    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));

    console.log('Process created:', {
      basicInfo,
      bpmnElements,
      selectedForms,
      fieldMappings,
    });

    setIsSaving(false);
    router.push('/process-management');
  };

  const handleSaveAsDraft = async () => {
    if (!isHydrated) return;

    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    console.log('Process saved as draft');
    setIsSaving(false);
    router.push('/process-management');
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:175:6" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="175" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:176:8" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="176" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-24%20bg-muted%20animate-pulse%22%7D" className="h-24 bg-muted animate-pulse"></div>
        <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:177:8" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="177" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20p-6%22%7D" className="max-w-7xl mx-auto p-6">
          <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:178:10" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="178" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%20animate-pulse%22%7D" className="h-96 bg-muted rounded-lg animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:185:4" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="185" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <WizardProgressHeader data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:186:6" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="186" data-component-file="WizardInteractive.tsx" data-component-name="WizardProgressHeader" data-component-content="%7B%22elementName%22%3A%22WizardProgressHeader%22%7D"
        currentStep={currentStep}
        steps={steps}
        onStepClick={handleStepClick}
        completedSteps={completedSteps}
      />

      <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:193:6" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="193" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20py-6%22%7D" className="max-w-7xl mx-auto py-6">
        {currentStep === 1 && (
          <BasicInfoStep data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:195:10" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="195" data-component-file="WizardInteractive.tsx" data-component-name="BasicInfoStep" data-component-content="%7B%22elementName%22%3A%22BasicInfoStep%22%7D"
            data={basicInfo}
            onDataChange={setBasicInfo}
            onValidationChange={setIsStepValid}
          />
        )}

        {currentStep === 2 && (
          <BPMNModelingStep data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:203:10" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="203" data-component-file="WizardInteractive.tsx" data-component-name="BPMNModelingStep" data-component-content="%7B%22elementName%22%3A%22BPMNModelingStep%22%7D"
            elements={bpmnElements}
            onElementsChange={setBpmnElements}
            onValidationChange={setIsStepValid}
          />
        )}

        {currentStep === 3 && (
          <FormAssociationStep data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:211:10" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="211" data-component-file="WizardInteractive.tsx" data-component-name="FormAssociationStep" data-component-content="%7B%22elementName%22%3A%22FormAssociationStep%22%7D"
            selectedForms={selectedForms}
            fieldMappings={fieldMappings}
            onFormsChange={setSelectedForms}
            onMappingsChange={setFieldMappings}
            onValidationChange={setIsStepValid}
          />
        )}

        {currentStep === 4 && (
          <ReviewStep data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:221:10" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="221" data-component-file="WizardInteractive.tsx" data-component-name="ReviewStep" data-component-content="%7B%22elementName%22%3A%22ReviewStep%22%7D"
            basicInfo={basicInfo}
            bpmnElements={bpmnElements}
            selectedForms={selectedForms}
            fieldMappings={fieldMappings}
            onEdit={setCurrentStep}
          />
        )}

        <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:230:8" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="230" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20px-6%20mt-6%22%7D" className="max-w-4xl mx-auto px-6 mt-6">
          <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:231:10" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="231" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
            <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:232:12" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="232" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-4%22%7D" className="flex items-center gap-4">
              <button data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:233:14" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="233" data-component-file="WizardInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-2%20border%20border-border%20rounded-md%20text-foreground%20hover%3Abg-muted%20transition-micro%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20flex%20items-center%20gap-2%22%2C%22textContent%22%3A%22Anterior%22%7D"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className="px-6 py-2 border border-border rounded-md text-foreground hover:bg-muted transition-micro disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Icon data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:238:16" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="238" data-component-file="WizardInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronLeftIcon%22%7D" name="ChevronLeftIcon" size={20} />
                Anterior
              </button>
              {lastSaved && (
                <span data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:242:16" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="242" data-component-file="WizardInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20flex%20items-center%20gap-2%22%2C%22textContent%22%3A%22Salvo%20automaticamente%20%C3%A0s%22%7D" className="text-sm text-muted-foreground flex items-center gap-2">
                  <Icon data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:243:18" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="243" data-component-file="WizardInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-success%22%7D" name="CheckCircleIcon" size={16} className="text-success" />
                  Salvo automaticamente às {lastSaved}
                </span>
              )}
            </div>

            <div data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:249:12" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="249" data-component-file="WizardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">
              <button data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:250:14" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="250" data-component-file="WizardInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-2%20border%20border-border%20rounded-md%20text-foreground%20hover%3Abg-muted%20transition-micro%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%2C%22textContent%22%3A%22Salvar%20Rascunho%22%7D"
                onClick={handleSaveAsDraft}
                disabled={isSaving}
                className="px-6 py-2 border border-border rounded-md text-foreground hover:bg-muted transition-micro disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Salvar Rascunho
              </button>

              {currentStep < steps.length ? (
                <button data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:259:16" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="259" data-component-file="WizardInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-2%20bg-accent%20text-accent-foreground%20rounded-md%20hover%3Abg-accent%2F90%20transition-micro%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20flex%20items-center%20gap-2%22%2C%22textContent%22%3A%22Pr%C3%B3ximo%22%7D"
                  onClick={handleNext}
                  disabled={!isStepValid}
                  className="px-6 py-2 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-micro disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Próximo
                  <Icon data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:265:18" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="265" data-component-file="WizardInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%7D" name="ChevronRightIcon" size={20} />
                </button>
              ) : (
                <button data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:268:16" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="268" data-component-file="WizardInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-2%20bg-primary%20text-primary-foreground%20rounded-md%20hover%3Abg-primary%2F90%20transition-micro%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20flex%20items-center%20gap-2%22%7D"
                  onClick={handleFinish}
                  disabled={isSaving}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-micro disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <Icon data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:275:22" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="275" data-component-file="WizardInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowPathIcon%22%2C%22className%22%3A%22animate-spin%22%7D" name="ArrowPathIcon" size={20} className="animate-spin" />
                      Finalizando...
                    </>
                  ) : (
                    <>
                      <Icon data-component-id="src/app/process-creation-wizard/components/WizardInteractive.tsx:280:22" data-component-path="src/app/process-creation-wizard/components/WizardInteractive.tsx" data-component-line="280" data-component-file="WizardInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%7D" name="CheckIcon" size={20} />
                      Finalizar Processo
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardInteractive;